import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { TextField, Autocomplete } from "@mui/material";
import { X } from "react-bootstrap-icons";
import api from "../../../../Axios.js";

const INITIAL_FILTERS = { department: "", manager: "", designation: "" };

const useFilterData = () => {
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [deptRes, desigRes, managerRes] = await Promise.all([
          api.get("/departments"),
          api.get("/lnd/get-designation-list"),
          api.get("/users/get-reporting-managers?_UserStatus=1"),
        ]);

        if (!cancelled) {
          setDepartments(deptRes.data?.data || []);
          setDesignations(desigRes.data?.data?.designations || []);
          setManagers(managerRes.data?.data || []);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  return { departments, designations, managers };
};

const EmployeeSelector = ({
  employees = [],
  selectedEmployeeIds = [],
  onChange,
}) => {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const { departments, designations, managers } = useFilterData();
  const selectedEmployeeCount = useRef(selectedEmployeeIds.length || 0);

  const handleSearchChange = useCallback(
    (e) => setSearch(e.target.value),
    []
  );

  const toggleEmployee = useCallback(
    (id) => {
      if(selectedEmployeeIds.includes(id)) {
        selectedEmployeeCount.current -= 1;
      }
      else {
        selectedEmployeeCount.current += 1;
      }
      const updated = selectedEmployeeIds.includes(id)
        ? selectedEmployeeIds.filter((e) => e !== id)
        : [...selectedEmployeeIds, id];

      onChange(updated);
    },
    [selectedEmployeeIds, onChange]
  );

  // ✅ FILTER + KEEP ALL EMPLOYEES
  const filteredEmployees = useMemo(() => {
    const { department, manager, designation } = filters;

    return employees.filter((emp) => {
      const name = `${emp.firstName ?? ""} ${emp.middleName ?? ""} ${emp.lastName ?? ""}`.toLowerCase();

      const nameMatch = name.includes(search.toLowerCase());
      const empIdMatch = emp._UserEmployeeID?.toString().toLowerCase().includes(search.toLowerCase());

      const deptMatch = !department || Number(emp.userDepartmentId) === Number(department);
      const managerMatch = !manager || Number(emp._ReportingManager) === Number(manager);
      const desigMatch = !designation || Number(emp.userDesignationId) === Number(designation);

      return (nameMatch || empIdMatch) && deptMatch && managerMatch && desigMatch;
    });
  }, [employees, search, filters]);

  // ✅ SORT → SELECTED FIRST
  const sortedEmployees = useMemo(() => {
    return [...filteredEmployees].sort((a, b) => {
      const aSelected = selectedEmployeeIds.includes(a._UserId);
      const bSelected = selectedEmployeeIds.includes(b._UserId);

      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  }, [filteredEmployees, selectedEmployeeIds]);

  // ✅ SELECT ALL (ONLY FILTERED)
  const filteredEmployeeIds = filteredEmployees.map((e) => e._UserId);

  const allFilteredSelected =
    filteredEmployeeIds.length > 0 &&
    filteredEmployeeIds.every((id) => selectedEmployeeIds.includes(id));

  const handleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      selectedEmployeeCount.current = 0;
      const updated = selectedEmployeeIds.filter(
        (id) => !filteredEmployeeIds.includes(id)
      );
      onChange(updated);
    } else {
      selectedEmployeeCount.current = filteredEmployeeIds.length || 0;
      const updated = Array.from(
        new Set([...selectedEmployeeIds, ...filteredEmployeeIds])
      );
      onChange(updated);
    }
  }, [allFilteredSelected, filteredEmployeeIds, selectedEmployeeIds, onChange]);

  return (
    <div className="card shadow-sm p-3 rounded-3 border-0">

      {/* Filters */}
      <div className="row g-6 mb-3">
        <div className="col-12 col-md-4">
          <TextField
            size="small"
            fullWidth
            label="Search Employee ID or Name"
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        <div className="col-12 col-md-4">
          <Autocomplete
            options={departments}
            getOptionLabel={(o) => o._DeptName || ""}
            onChange={(e, val) =>
              setFilters((p) => ({ ...p, department: val?._DeptID || "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Department" size="small" />
            )}
          />
        </div>

        {/* <div className="col-12 col-md-3">
          <Autocomplete
            options={designations}
            getOptionLabel={(o) => o._DesigName || ""}
            onChange={(e, val) =>
              setFilters((p) => ({ ...p, designation: val?._DesigID || "" }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Designation" size="small" />
            )}
          />
        </div> */}

        <div className="col-12 col-md-4">
          <Autocomplete
            options={managers}
            getOptionLabel={(o) =>
              `${o._FirstName ?? ""} ${o._LastName ?? ""}`
            }
            onChange={(e, val) =>
              setFilters((p) => ({
                ...p,
                manager: val?._UserID?.toString() || "",
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Manager" size="small" />
            )}
          />
        </div>
      </div>

      {/* Select All */}
      <div className="d-flex align-items-center mb-2">
        <input
          type="checkbox"
          checked={allFilteredSelected}
          onChange={handleSelectAll}
        />
        <label className="ms-2">{`${allFilteredSelected ? 'Selected All' : 'Select All'} (${selectedEmployeeCount.current}) Employees`}</label>
      </div>

      {/* Employee List */}
      <div style={{ maxHeight: 250, overflowY: "auto", border: "1px solid #eee" }}>
        {sortedEmployees.length === 0 ? (
          <p className="text-center py-3">No employees found</p>
        ) : (
          sortedEmployees.map((emp) => {
            const isSelected = selectedEmployeeIds.includes(emp._UserId);

            return (
              <label
                key={emp._UserId}
                className="d-flex gap-2 p-2 border-bottom"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleEmployee(emp._UserId)}
                />

                <span>
                  {emp.firstName} {emp.middleName} {emp.lastName}
                  <small className="text-muted ms-1">
                    ({emp._UserEmployeeID})
                  </small>
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EmployeeSelector;